import { useCallback, useState } from 'react';

import { noop } from 'lodash-es';

import { IServerOrder } from '@rbi-ctg/menu';
import { IPlaceCateringOrderInput, usePlaceCateringOrderMutation } from 'generated/rbi-graphql';
import { IUseCommitOrderParams } from 'hooks/commit-order';
import { useOrderStatus } from 'hooks/order-status';
import { useOrderContext } from 'state/order';
import { OrderSuccessFailureStatuses } from 'state/order/constants';
import { dateTimeSelectorToUTC } from 'utils/dateTime';
import { PerformanceMarks, setMark } from 'utils/timing';

const ORDER_DURATION_ACTION = 'place-catering-order';
// default order status polling interval, in ms
const DEFAULT_POLL_INTERVAL = 1500;

const usePlaceCateringOrder = ({
  logger,
  orderStatusPollInterval = DEFAULT_POLL_INTERVAL,
  onCompleted = noop,
  rbiOrderId,
  skipOrderStatusPolling = true,
}: IUseCommitOrderParams) => {
  const [skipPolling, setSkipPolling] = useState(skipOrderStatusPolling);
  const [fireMutation, mutationResult] = usePlaceCateringOrderMutation();
  const { logOrderLatencyDuration, onCommitSuccess } = useOrderContext();

  const placeCateringOrder = useCallback(
    async (rawInput: IPlaceCateringOrderInput) => {
      const pickupTime = dateTimeSelectorToUTC(rawInput.cateringInfo.pickupTime);
      const input = { ...rawInput, cateringInfo: { pickupTime } };
      const result = await fireMutation({
        variables: {
          input,
        },
      });
      if (result.data) {
        setSkipPolling(false);
      }
      return result;
    },
    [fireMutation]
  );

  const logOrderLatency = useCallback(
    (rbiOrder: IServerOrder) => {
      setMark(PerformanceMarks.COMMIT_END);

      logOrderLatencyDuration(ORDER_DURATION_ACTION, rbiOrder);
    },
    [logOrderLatencyDuration]
  );

  const onSuccess = useCallback(
    (rbiOrder: IServerOrder) => {
      onCompleted();
      onCommitSuccess(rbiOrder);
    },
    [onCommitSuccess, onCompleted]
  );

  const queryResult = useOrderStatus({
    failureStatuses: {
      catering: OrderSuccessFailureStatuses.failures.catering,
      delivery: [],
      pos: [],
    },
    logger,
    logOrderLatency,
    orderStatusPollInterval,
    rbiOrderId,
    onSuccess,
    successStatuses: {
      catering: OrderSuccessFailureStatuses.success.catering,
      delivery: [],
      pos: [],
    },
    skip: skipPolling,
  });

  return {
    failure: queryResult.failure,
    loading: mutationResult.loading || queryResult.loading,
    placeCateringOrder,
    result: mutationResult,
    success: queryResult.success,
  };
};

export default usePlaceCateringOrder;
